<script setup>
import { ref, computed, onMounted, defineEmits, watch } from 'vue';
import { currentModel } from '../forms/currentModel';
import { Notify, Loading, Dialog } from 'quasar';
import { useRouter, useRoute } from 'vue-router/composables';

import { xios } from '../forms/tempAxios';

import Forms from '../forms/Forms.vue';
import BookingAddTrip from './BookingAddTrip.vue';
import ViewTripDetails from './ViewTripDetails.vue';

const customerListFetched = ref(false);

const create = ref([
  {
    is: 'TSeparator',
    label: 'Basic Details',
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TSelect',
        icon: 'search',
        Label: 'Choose Customer*',
        options: computed(() => {
          return customerList.value;
        }),
        clearable: true,
        emitValue: true,
        mapOptions: true,
        'use-input': true,
        'option-value': 'id',
        'option-label': 'companyName',
        model: 'customerId',
      },
      {
        is: 'TInput',
        Label: 'Booking Ref Number',
        type: 'text',
        disable: true,
        model: 'referenceNumber',
      },
      // {
      //   is: 'TSelect',
      //   Label: 'Choose Sub-Customer',
      //   icon: 'keyboard_arrow_down',
      //   disable: true,
      //   model: 'subCustomer',
      // },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TSelect',
        Label: 'Booking Type',
        icon: 'keyboard_arrow_down',
        'use-input': true,
        options: ['Standard', 'Member', 'Exclusive'],
        model: 'bookingType',
      },
    ],
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TRadio',
        Label: 'Order Type',
        model: 'orderType',
        options: [{ label: 'Shipment' }, { label: 'Dedicated' }],
        // disable: true,
      },
      {
        is: 'TRadio',
        Label: 'Type of Delivery',
        model: 'deliveryType',
        options: [{ label: 'Door to Door' }, { label: 'Multi Trips' }],
      },
    ],
  },

  // {
  //   is: 'Row',
  //   ui: [
  //     {
  //       is: 'Row',
  //       row: true,
  //       class: 'flex-no-wrap',
  //       ui: [
  //         // {
  //         //   is: 'TSelect',
  //         //   Label: 'Type of Trip / Shipment',
  //         //   options: ['Drop Deliver', 'Charted Delivery'],
  //         //   icon: 'keyboard_arrow_down',
  //         //   'use-input': true,
  //         //   model: 'tripType',
  //         // },
  //         {
  //           is: 'TInput',
  //           Label: 'Quantity',
  //           type: 'number',
  //           class: 'w-24 ',
  //           noFlex: true,
  //           // disable: true,
  //           model: 'quantity',
  //         },
  //       ],
  //     },
  //     {
  //       is: 'TInput',
  //       Label: 'Booking Ref Number',
  //       type: 'number',
  //       disable: true,
  //       model: 'refNo',
  //     },
  //   ],
  // },
  {
    is: 'Row',
    ui: [
      {
        is: 'Row',
        row: true,
        class: 'flex-no-wrap',
        ui: [
          {
            is: 'TInput',
            Label: 'Man Power',
            type: 'number',
            model: 'manPower',
          },
          {
            is: 'TInput',
            Label: 'Man Power Charges',
            type: 'number',
            model: 'manPowerCharges',
          },
        ],
      },
    ],
  },
  {
    is: 'TSeparator',
    label: 'Truck Requirements',
  },
  {
    is: 'Row',
    ui: [
      {
        is: 'TSelect',
        Label: 'Truck Type',
        icon: 'keyboard_arrow_down',
        'use-input': true,
        options: ['Box', 'Panel', 'Tail Lift'],
        model: 'truckType',
      },
      {
        is: 'TSelect',
        Label: 'Truck Tonnage',
        icon: 'keyboard_arrow_down',
        'use-input': true,
        options: ['1 Ton Truck', '3 Ton Truck', '5 Ton Truck'],
        model: 'truckSize',
      },
    ],
  },
]);

const customerList = ref([]);
onMounted(async () => {
  if (!currentModel.value) {
    currentModel.value = {};
  }
  // Fetch lists needed for dropdowns etc.
  customerList.value = await fetchData();
  driverList.value = await fetchDriver();

  // --- Check for Edit Mode Trigger ---
  const storedBookingId = localStorage.getItem('currentBookingId');
  const routeBookingId = route.params.id;

  if (storedBookingId) {
    console.log('Found booking ID in localStorage:', storedBookingId);
    bookingId.value = storedBookingId;
    isUpdateMode.value = true;
    await fetchBookingDetails(storedBookingId); // Load data using stored ID
    localStorage.removeItem('currentBookingId'); // Clear after use
  } else if (routeBookingId) {
    console.log('Found booking ID in route params:', routeBookingId);
    bookingId.value = routeBookingId;
    isUpdateMode.value = true;
    await fetchBookingDetails(routeBookingId); // Load data using route ID
  } else {
    console.log('No booking ID found, initializing create mode.');
    resetForm(); // Ensure form is reset for creation
  }
});

watch(
  () => (route && route.params ? route.params.id : null),
  async (newId) => {
    // Handle direct navigation to edit route *after* initial mount
    if (newId && newId !== bookingId.value) {
      // Only reload if ID actually changes
      console.log('Route parameter ID changed to:', newId);
      bookingId.value = newId;
      isUpdateMode.value = true;
      await fetchBookingDetails(newId);
    } else if (!newId && isUpdateMode.value) {
      // Navigated away from edit route (e.g., to create view via sidebar)
      // Check if bookingId.value exists to ensure we were actually in edit mode
      if (bookingId.value) {
        console.log('Navigated away from edit route, resetting form.');
        resetForm();
      }
    }
  },
);

async function fetchData() {
  try {
    const res = await xios.post('api-tms/script-pagination-standard', {
      model: 'Customer',
      itemsPerPage: 10,
      page: 1,
      sortBy: 'companyName',
      orderBy: 'ASC',
      searchQuery: '',
      searchFields: ['companyName', 'companyRegistration', 'contactPerson'],
      attributesInclude: [],
      includeModels: [
        {
          model: 'Poi',
        },
      ],
      filterFields: [
        {
          field: 'status',
          operator: 'eq',
          value: 1,
        },
        {
          field: 'clientId',
          operator: 'eq',
          value: JSON.parse(localStorage.getItem('userInfo')).uid,
        },
      ],
    });
    if (res.data.success) {
      return res.data.data.items;
    }
  } catch (error) {
    console.log(error);
    return [];
  } finally {
    customerListFetched.value = true;
  }
}

const driverList = ref([]);
async function fetchDriver() {
  try {
    const res = await xios.post('api-tms/script-pagination-standard', {
      model: 'Driver',
      page: 1,
      itemsPerPage: 10,
      filterFields: [
        {
          field: 'status',
          operator: 'eq',
          value: 1,
        },
        {
          field: 'clientId',
          operator: 'eq',
          value: JSON.parse(localStorage.getItem('userInfo')).uid,
        },
      ],
      // includeModels: [
      //   {
      //     model: 'TripDriverAssignment',
      //     as: 'trip_driver_assignments', // Use the association alias if defined
      //   },
      // ],
      attributesInclude: [],
      sortBy: 'licenseExpiry',
      orderBy: 'ASC',
    });
    if (res.data.success) {
      return res.data.data.items;
    }
  } catch (error) {
    console.log(error);
    return [];
  }
}

const userInfo = ref(null);

onMounted(() => {
  userInfo.value = JSON.parse(localStorage.getItem('userInfo'));
  currentModel.value = {
    customerId: null,
    clientId: null,
    referenceNumber: null,
    bookingType: null,
    orderType: null,
    deliveryType: null,
    bookingDate: null,
    bookingTime: null,
    status: null,
    truckType: null,
    truckSize: null,
    manPower: null,
    manPowerCharges: null,
    meta: {
      priority: null,
      specialInstructions: null,
      contactName: null,
      contactPhone: null,
      contactEmail: null,
      notes: null,
    },
    trips: [],
    temp: {},
  };
});

function buildBookingPayload() {
  const bookingMeta = { ...currentModel.value.meta }; // Copy booking-level meta
  // We might not need to include trips in the booking meta,
  // but ensure other fields like priority, instructions etc. are preserved.
  // delete bookingMeta.trips; // Example if trips were incorrectly nested here

  const payload = {
    customerId: currentModel.value.customerId,
    clientId: userInfo.value && userInfo.value.uid ? userInfo.value.uid : 1,
    bookingType: currentModel.value.bookingType,
    orderType: currentModel.value.orderType,
    deliveryType: currentModel.value.deliveryType,
    truckType: currentModel.value.truckType,
    truckTonnage: currentModel.value.truckSize,
    manPower: currentModel.value.manPower,
    manPowerCharges: currentModel.value.manPowerCharges,
    referenceNumber: currentModel.value.referenceNumber, // Include if needed for update?
    meta: bookingMeta, // Use the cleaned booking meta
    trips: [],
  };

  if (currentModel.value.trips && Array.isArray(currentModel.value.trips)) {
    payload.trips = currentModel.value.trips.map((trip) => {
      // Construct the trip data object for the payload,
      // ensuring it matches the structure created by BookingAddTrip's addTrip function
      const tripData = {
        // Include id only if it exists (for updates)
        ...(trip.id && { id: trip.id }),
        tripType: trip.tripType,
        estimatedJobStartDate: trip.estimatedJobStartDate,
        estimatedJobStartTime: trip.estimatedJobStartTime,
        driverId: trip.driverId, // Send driverId (could be null)
        tripNotes: trip.tripNotes, // Include notes if they exist
        // Directly use the locations array from the current trip model
        // This assumes the locations array has the simplified structure (poiId, type, primary, sequence)
        locations: trip.locations
          ? trip.locations.map((loc) => ({
              poiId: loc.poiId,
              locationType: loc.locationType,
              primary: loc.primary,
              sequence: loc.sequence,
              // Ensure no meta is included here
            }))
          : [],
        // Include the trip-level meta object containing POI details etc.
        meta: trip.meta || {}, // Include the whole meta object from the trip
      };
      // REMOVED the complex location reconstruction logic
      return tripData;
    });
  }

  // Add booking ID for updates
  if (isUpdateMode.value && bookingId.value) {
    payload.id = bookingId.value;
  }

  return payload;
}

const loading = ref(false);
const submitBooking = async () => {
  try {
    loading.value = true;
    const payload = buildBookingPayload();
    console.log('Submitting booking with payload:', payload);

    const response = await xios.post('api-tms/bookings', payload);
    if (response.data.success) {
      alert('Booking created successfully!');
      // Reset form or redirect as needed
    } else {
      alert('Failed to create booking: ' + response.data.message);
    }
  } catch (error) {
    console.error('Error creating booking:', error);
    alert(
      'Error creating booking: ' +
        ((error.response &&
          error.response.data &&
          error.response.data.message) ||
          error.message),
    );
  } finally {
    loading.value = false;
  }
};

const bookings = ref([]);
const tab = ref();

// Ref for the BookingAddTrip component instance
const bookingAddTripRef = ref(null);

// Helper to get primary location details for display
function getPrimaryLocation(trip) {
  if (!trip || !trip.locations || trip.locations.length === 0 || !trip.meta)
    return null;

  const primaryLoc = trip.locations.find((loc) => loc.primary);
  if (!primaryLoc) return null;

  const poiId = primaryLoc.poiId;
  // Infer type if not explicitly set in location object
  const locType =
    primaryLoc.locationType ||
    (trip.tripType === 'pickup' ? 'pickup' : 'dropoff');

  const name =
    trip.meta.poi && trip.meta.poi.id === poiId
      ? trip.meta.poi.name
      : 'Unknown Primary';
  const address = trip.meta.address;

  return { name, address, locationType: locType }; // Return locationType
}

// Helper to get secondary (dropoff for pickup) location details for display
function getSecondaryLocation(trip) {
  if (
    !trip ||
    trip.tripType !== 'pickup' ||
    !trip.locations ||
    trip.locations.length < 2 ||
    !trip.meta
  )
    return null;

  const secondaryLoc = trip.locations.find(
    (loc) => !loc.primary && loc.locationType === 'dropoff',
  );
  if (!secondaryLoc) return null;

  const poiId = secondaryLoc.poiId;

  const name =
    trip.meta.dropoffPoi && trip.meta.dropoffPoi.id === poiId
      ? trip.meta.dropoffPoi.name
      : 'Unknown Dropoff';
  const address = trip.meta.dropoffAddress;

  return { name, address, locationType: 'dropoff' }; // Return locationType
}

// Helper to get driver name
function getDriverName(trip) {
  if (!trip.driverId) return 'Not Assigned';
  // Prefer name from meta if available - replace optional chaining
  if (trip.meta && trip.meta.driverName) return trip.meta.driverName;
  // Replace optional chaining
  if (trip.meta && trip.meta.driver && trip.meta.driver.name)
    return trip.meta.driver.name;
  // Fallback to searching the driver list
  const driver = driverList.value.find((d) => d.id === trip.driverId);
  return driver ? driver.name : 'Assigned (Unknown Name)';
}

// --- ADD MISSING REFS ---
const isUpdateMode = ref(false);
const isLoading = ref(false);
const bookingId = ref(null); // Needed for update mode

// --- ADD EMITS DEFINITION ---
const emit = defineEmits(['creation-success', 'update-success']);

// --- router/route/userInfo --- (Router/Route needed for logic below, userInfo likely already defined)
const route = useRoute();
const router = useRouter();
// const userInfo = ref(JSON.parse(localStorage.getItem('userInfo'))); // Remove duplicate - Assuming original exists elsewhere

// --- bookingStatus/tripsInProgress/canEditFields ---
const bookingStatus = ref(null);
const tripsInProgress = ref(false);
const canEditFields = computed(() => {
  if (!isUpdateMode.value) return true;
  if (
    bookingStatus.value === 'in-progress' ||
    bookingStatus.value === 'completed' ||
    bookingStatus.value === 'cancelled'
  )
    return false;
  return true;
});

// --- ADD MISSING VALIDATION FUNCTION ---
function validateBookingForm() {
  let errors = [];
  if (!currentModel.value.customerId) {
    errors.push('Please select a customer.');
  }
  if (!currentModel.value.trips || currentModel.value.trips.length === 0) {
    errors.push('Please add at least one trip to the booking.');
  }
  return errors;
}

// --- ADD MISSING SUBMIT FUNCTION ---
async function submitBookingData() {
  const validationErrors = validateBookingForm();
  if (validationErrors.length > 0) {
    validationErrors.forEach((error) => {
      Notify.create({ message: error, color: 'negative' });
    });
    return;
  }

  isLoading.value = true;
  Loading.show({
    message: isUpdateMode.value ? 'Updating booking...' : 'Creating booking...',
  });

  try {
    const bookingPayload = buildBookingPayload();
    console.log('Submitting Payload:', JSON.stringify(bookingPayload, null, 2));

    let response;
    if (isUpdateMode.value) {
      console.log('Calling Update API');
      response = await xios.post('api-tms/script-update-standard', {
        model: 'Booking',
        options: bookingPayload,
      });
    } else {
      console.log('Calling Create API');
      response = await xios.post('api-tms/script-insert-standard', {
        model: 'Booking',
        options: bookingPayload,
      });
    }

    console.log('API Response:', response.data);

    if (response.data.success) {
      Notify.create({
        message: isUpdateMode.value
          ? 'Booking updated successfully!'
          : 'Booking created successfully!',
        color: 'positive',
      });

      if (isUpdateMode.value) {
        emit('update-success', 'Manage Booking');
      } else {
        resetForm(); // Call existing resetForm
        emit('creation-success', 'Manage Booking');
      }
    } else {
      // Replace optional chaining in error handling
      let errorDetail =
        response.data.message ||
        (response.data.error
          ? (response.data.error.details && response.data.error.details[0]) ||
            response.data.error.message
          : 'Unknown API error');
      throw new Error(errorDetail);
    }
  } catch (error) {
    console.error('Error with booking operation:', error);
    Notify.create({
      message: `Operation failed: ${error.message || 'Unknown error'}`,
      color: 'negative',
      multiLine: true,
      timeout: 5000,
    });
  } finally {
    isLoading.value = false;
    Loading.hide();
  }
}

// --- ADD MISSING CANCEL FUNCTION ---
function cancelBookingOperation() {
  console.log('Cancel operation triggered. Update Mode:', isUpdateMode.value);
  if (isUpdateMode.value) {
    emit('update-success', 'Manage Booking');
  } else {
    resetForm(); // Call existing resetForm
    emit('creation-success', 'Manage Booking');
  }
}

// --- Add definition for fetchBookingDetails ---
async function fetchBookingDetails(id) {
  isLoading.value = true;
  Loading.show({ message: 'Loading booking details...' });
  try {
    const response = await xios.post('api-tms/script-pagination-standard', {
      model: 'Booking',
      page: 1,
      itemsPerPage: 1,
      filterFields: [{ field: 'id', operator: 'eq', value: id }],
      // Ensure necessary includes are present
      includeModels: [
        {
          model: 'Trip',
          include: [
            // Include necessary Trip relations if needed, e.g., Locations, DriverAssignments
            // { model: 'Location', as: 'locations' },
            { model: 'TripDriverAssignment', as: 'trip_driver_assignments' },
          ],
        },
        { model: 'Customer' },
      ],
    });
    if (response.data.success && response.data.data.items.length > 0) {
      const booking = response.data.data.items[0];
      bookingStatus.value = booking.status;
      // Map booking data carefully
      currentModel.value = {
        ...booking,
        customerId: booking.customerId, // Ensure this is mapped
        clientId: booking.clientId, // Ensure this is mapped
        referenceNumber: booking.referenceNumber,
        truckTonnage: booking.truckTonnage || booking.truckSize,
        meta: booking.meta || {},
        // Map trips correctly, ensuring locations array exists
        trips: booking.trips
          ? booking.trips.map((trip) => ({
              ...trip,
              locations: trip.locations || [], // Ensure locations array exists
              meta: trip.meta || {},
            }))
          : [],
        temp: {},
      };
      console.log('Loaded Booking:', currentModel.value);
      console.log(
        'Loaded Booking Data:',
        JSON.stringify(currentModel.value, null, 2),
      ); // Log mapped data
      console.log(
        `Number of trips mapped: ${currentModel.value.trips ? currentModel.value.trips.length : 0}`,
      ); // Log trip count
    } else {
      Notify.create({
        message: 'Booking details not found or failed to load',
        color: 'negative',
      });
      router.push('/tms/live'); // Or relevant list page
    }
  } catch (error) {
    console.error('Error fetching booking details:', error);
    Notify.create({
      message: `Error loading booking: ${error.message}`,
      color: 'negative',
    });
  } finally {
    isLoading.value = false;
    Loading.hide();
  }
}

// --- Add definition for resetForm ---
function resetForm() {
  console.log('Resetting form');
  currentModel.value = {
    customerId: null,
    clientId: (userInfo.value && userInfo.value.uid) || 1,
    referenceNumber: null,
    bookingType: null,
    orderType: null,
    deliveryType: null,
    bookingDate: null, // May need defaults?
    bookingTime: null, // May need defaults?
    status: null, // Default status?
    truckType: null,
    truckTonnage: null,
    manPower: null,
    manPowerCharges: null,
    meta: {
      priority: null,
      specialInstructions: null,
      contactName: null,
      contactPhone: null,
      contactEmail: null,
      notes: null,
    },
    trips: [],
    temp: {},
  };
  bookingId.value = null;
  isUpdateMode.value = false;
  bookingStatus.value = null;
  tripsInProgress.value = false;
  // Potentially reset customerList/driverList if they should refresh?
}

// --- ADD TRIP DELETION HANDLER ---
function handleTripDeleteRequest(index) {
  console.log(`CreateBooking: Received request-delete for index ${index}`);
  if (
    currentModel.value &&
    currentModel.value.trips &&
    index >= 0 &&
    index < currentModel.value.trips.length
  ) {
    // Use Dialog.create here in the parent
    Dialog.create({
      title: 'Confirm Deletion',
      message: 'Are you sure you want to remove this trip?',
      cancel: true,
      persistent: true,
    })
      .onOk(() => {
        console.log(`CreateBooking: Deleting trip at index ${index}`);
        currentModel.value.trips.splice(index, 1);
        Notify.create({
          message: 'Trip removed successfully',
          color: 'positive',
          position: 'top',
        });
      })
      .onCancel(() => {
        console.log('CreateBooking: Trip deletion cancelled.');
      });
  } else {
    console.error(
      'handleTripDeleteRequest received invalid index or missing trips array:',
      index,
    );
    Notify.create({
      message: 'Error: Could not remove trip (invalid index).',
      color: 'negative',
    });
  }
}

// --- ADD STATUS HELPER FUNCTIONS ---
function formatStatus(status) {
  if (!status) return 'UNASSIGNED';
  return status.replace('-', ' ').toUpperCase();
}

function getStatusColor(status) {
  if (!status) return 'orange';
  switch (status.toLowerCase()) {
    case 'in-progress':
      return 'blue';
    case 'completed':
      return 'green';
    case 'cancelled':
      return 'red';
    default:
      return 'orange';
  }
}

function getStatusIcon(status) {
  if (!status) return 'mdi-help-circle';
  switch (status.toLowerCase()) {
    case 'in-progress':
      return 'mdi-progress-clock';
    case 'completed':
      return 'mdi-check-circle';
    case 'cancelled':
      return 'mdi-cancel';
    default:
      return 'mdi-clock-outline';
  }
}

// --- ADD STATUS DETERMINATION LOGIC ---
function getTripStatus(trip) {
  // Explicit trip.status takes priority if it's a final state
  const tripStatus = trip.status ? trip.status.toLowerCase() : null;
  if (['completed', 'cancelled'].includes(tripStatus)) {
    return tripStatus;
  }

  // Check driver assignments for more detailed status
  const assignment =
    trip.trip_driver_assignments && trip.trip_driver_assignments.length > 0
      ? trip.trip_driver_assignments[0]
      : null;
  if (assignment) {
    // Assignment status takes priority if defined and not just 'assigned'
    const assignmentStatus = assignment.status
      ? assignment.status.toLowerCase()
      : null;
    if (assignmentStatus && assignmentStatus !== 'assigned') {
      return assignmentStatus; // e.g., in-transit, delivered
    }
    // Check for location data to determine if in-progress
    if (assignment.driverLat && assignment.driverLng) {
      return 'in-progress';
    }
    // If driverId exists but no further status, it's 'assigned'
    if (assignment.driverId) {
      return 'assigned';
    }
  }

  // Fallback to trip.status if it exists (could be assigned/unassigned)
  if (tripStatus) {
    return tripStatus;
  }

  // Default to unassigned
  return 'unassigned';
}

// Helpers based on status
const canEditTrip = (trip) => getTripStatus(trip) === 'unassigned';
const canDeleteTrip = (trip) => getTripStatus(trip) === 'unassigned';
const canReassignTrip = (trip) => {
  const status = getTripStatus(trip);
  return ['assigned', 'in-progress', 'in-transit'].includes(status);
};

// --- ADD REASSIGNMENT DIALOG STATE & FUNCTIONS ---
const reassignDialog = ref(false);
const tripToReassign = ref(null);
const selectedDriver = ref(null);

function openReassignDialog(trip) {
  tripToReassign.value = trip;
  const currentAssignment =
    trip.trip_driver_assignments && trip.trip_driver_assignments.length > 0
      ? trip.trip_driver_assignments[0]
      : null;
  selectedDriver.value =
    (currentAssignment ? currentAssignment.driverId : null) ||
    trip.driverId ||
    null;
  reassignDialog.value = true;
}

async function confirmReassignment() {
  if (!tripToReassign.value || !tripToReassign.value.id) {
    Notify.create({
      type: 'negative',
      message: 'Cannot reassign: Trip data missing.',
    });
    return;
  }

  try {
    Loading.show({ message: 'Updating driver assignment...' });

    await xios.post('api-tms/script-update-standard', {
      model: 'Trip',
      options: {
        id: tripToReassign.value.id,
        driverId: selectedDriver.value,
      },
    });

    Notify.create({
      type: 'positive',
      message: 'Driver assignment updated successfully',
    });

    if (isUpdateMode.value && bookingId.value) {
      await fetchBookingDetails(bookingId.value);
    }
  } catch (error) {
    console.error('Reassignment Error:', error);
    Notify.create({
      type: 'negative',
      message: 'Failed to update driver assignment',
      caption:
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message,
    });
  } finally {
    Loading.hide();
    reassignDialog.value = false;
    tripToReassign.value = null;
  }
}

// --- Add State for View Dialog ---
const viewTripDialogVisible = ref(false);
const tripToView = ref(null);

function openViewDialog(trip) {
  tripToView.value = trip;
  viewTripDialogVisible.value = true;
}
</script>
<template>
  <div class="fit flex flex-col overflow-auto p-8">
    <!-- Basic Booking Details Section -->
    <div class="flex flex-wrap">
      <div class="w-full lg:w-1/2 flex flex-col gap-4 px-2">
        <div class="text-xl font-semibold text-primary">
          {{ isUpdateMode ? 'Update Booking' : $attrs.selected }}
        </div>
        <template v-if="customerListFetched">
          <Forms :ui="create" />
        </template>
        <div
          v-else
          class="flex items-center justify-center"
          style="min-height: 200px"
        >
          <q-inner-loading :showing="!customerListFetched" dark>
            <q-spinner-gears size="50px" color="primary" />
            <div class="q-mt-md text-grey">Loading Customer Data...</div>
          </q-inner-loading>
        </div>
        <q-separator spaced class="my-8" />
      </div>

      <!-- Trip Management Section -->
      <div class="w-full lg:w-1/2 flex flex-col gap-4 px-2">
        <div class="text-xl font-semibold text-primary mb-4">Trip Details</div>

        <!-- Placeholder if no customer selected -->
        <div
          v-if="!currentModel.customerId"
          class="flex items-center justify-center h-40"
        >
          <q-card flat bordered class="text-center p-4 w-full">
            <q-card-section>
              <div class="text-h6 text-grey">Trips</div>
              <p class="text-grey-7 q-mt-md">
                Please select a customer first to add trips
              </p>
            </q-card-section>
          </q-card>
        </div>

        <!-- BookingAddTrip component (now only for the Add/Edit modal trigger) -->
        <BookingAddTrip
          v-if="currentModel.customerId"
          ref="bookingAddTripRef"
          :customerList="customerList"
          :driverList="driverList"
          :isUpdateMode="isUpdateMode"
          :bookingStatus="bookingStatus"
          :canEditFields="canEditFields"
          @request-delete="handleTripDeleteRequest"
        />

        <!-- The scrollable trip list container -->
        <div
          v-if="
            currentModel.customerId &&
            currentModel.trips &&
            currentModel.trips.length > 0
          "
          class="trip-timeline-container q-mt-md"
          style="padding-right: 8px"
        >
          <q-card
            v-for="(trip, index) in currentModel.trips"
            :key="trip.id || index"
            class="q-mb-sm trip-card"
            bordered
            flat
          >
            <q-card-section
              class="flex justify-between items-start q-py-sm q-px-md"
            >
              <div>
                <!-- Status Badge -->
                <q-badge
                  :color="getStatusColor(trip.status)"
                  class="status-badge q-mr-sm q-mb-xs"
                >
                  {{ formatStatus(trip.status) }}
                </q-badge>
                <!-- Trip Type Badge -->
                <q-badge
                  :color="trip.tripType === 'pickup' ? 'blue' : 'green'"
                  class="q-mr-sm q-mb-xs"
                >
                  {{ trip.tripType === 'pickup' ? 'Pickup' : 'Dropoff' }}
                </q-badge>
                <div class="text-caption text-grey">
                  {{ trip.estimatedJobStartDate || 'No Date' }} -
                  {{ trip.estimatedJobStartTime || 'No Time' }}
                </div>
                <!-- Primary Location -->
                <div v-if="getPrimaryLocation(trip)" class="q-mt-xs">
                  <div class="text-weight-medium">
                    {{
                      getPrimaryLocation(trip).locationType === 'pickup'
                        ? 'Pickup'
                        : 'Dropoff'
                    }}
                    Location:
                    {{ getPrimaryLocation(trip).name || 'Primary Location' }}
                  </div>
                  <div class="text-caption text-grey-7">
                    {{ getPrimaryLocation(trip).address || 'No address' }}
                  </div>
                </div>
                <!-- Secondary Location (Dropoff for Pickup) -->
                <div v-if="getSecondaryLocation(trip)" class="q-mt-xs">
                  <div class="text-weight-medium text-primary">
                    {{
                      getSecondaryLocation(trip).locationType === 'pickup'
                        ? 'Pickup'
                        : 'Dropoff'
                    }}
                    Location:
                    {{ getSecondaryLocation(trip).name || 'Dropoff Location' }}
                  </div>
                  <div class="text-caption text-grey-7">
                    {{ getSecondaryLocation(trip).address || 'No address' }}
                  </div>
                </div>
                <!-- Driver Info -->
                <div class="q-mt-xs">
                  <span class="text-weight-medium">Driver: </span>
                  <span :class="{ 'text-grey': !trip.driverId }">{{
                    getDriverName(trip)
                  }}</span>
                </div>
              </div>
              <!-- Action Buttons - Conditionally Rendered -->
              <div class="flex items-center justify-end q-ml-sm q-gutter-xs">
                <!-- Edit Button (Only for unassigned) -->
                <q-btn
                  v-if="canEditTrip(trip)"
                  flat
                  dense
                  round
                  icon="edit"
                  color="primary"
                  size="sm"
                  @click="
                    bookingAddTripRef && bookingAddTripRef.edit(index, trip)
                  "
                >
                  <q-tooltip>Edit Trip</q-tooltip>
                </q-btn>

                <!-- Reassign Button (Only for assigned/in-progress) -->
                <q-btn
                  v-if="canReassignTrip(trip)"
                  flat
                  dense
                  round
                  icon="switch_account"
                  color="warning"
                  size="sm"
                  @click="openReassignDialog(trip)"
                >
                  <q-tooltip>Reassign Driver</q-tooltip>
                </q-btn>

                <!-- Delete Button (Only for unassigned) -->
                <q-btn
                  v-if="canDeleteTrip(trip)"
                  flat
                  dense
                  round
                  icon="delete"
                  color="negative"
                  size="sm"
                  @click="
                    bookingAddTripRef && bookingAddTripRef.removeTrip(index)
                  "
                >
                  <q-tooltip>Delete Trip</q-tooltip>
                </q-btn>

                <!-- View Button (Updated) -->
                <q-btn
                  v-if="!canEditTrip(trip) && !canDeleteTrip(trip)"
                  flat
                  dense
                  round
                  icon="visibility"
                  color="grey"
                  size="sm"
                  @click="openViewDialog(trip)"
                >
                  <q-tooltip>View Details</q-tooltip>
                </q-btn>
              </div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Placeholder if customer selected but no trips added -->
        <div
          v-if="
            currentModel.customerId &&
            (!currentModel.trips || currentModel.trips.length === 0)
          "
          class="flex items-center justify-center h-40"
        >
          <q-card flat bordered class="text-center p-4 w-full">
            <q-card-section>
              <div class="text-h6 text-grey">No Trips Added</div>
              <p class="text-grey-7 q-mt-md">
                Click "Add Trip" below to get started.
              </p>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </div>

    <!-- Submit/Cancel Buttons -->
    <div class="q-pa-md q-mt-auto row justify-end q-gutter-sm">
      <!-- q-mt-auto pushes buttons to bottom in flex-col -->
      <q-btn
        flat
        label="Cancel"
        color="primary"
        @click="cancelBookingOperation"
      />
      <q-btn
        :label="isUpdateMode ? 'Update Booking' : 'Create Booking'"
        color="primary"
        @click="submitBookingData"
        :loading="isLoading"
      />
    </div>

    <!-- Driver Reassignment Dialog -->
    <q-dialog v-model="reassignDialog" persistent>
      <q-card style="min-width: 350px">
        <q-card-section class="row items-center">
          <q-icon
            name="mdi-account-switch"
            color="warning"
            size="md"
            class="q-mr-sm"
          />
          <span class="text-h6">Change Driver Assignment</span>
        </q-card-section>

        <q-card-section v-if="tripToReassign">
          <p class="text-caption">
            Updating driver for Trip ID: {{ tripToReassign.id }}
          </p>
          <q-select
            v-model="selectedDriver"
            :options="driverList"
            label="Select New Driver"
            option-label="name"
            option-value="id"
            emit-value
            map-options
            outlined
            dense
            clearable
          >
            <template v-slot:no-option>
              <q-item>
                <q-item-section class="text-grey">
                  No drivers available
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-checkbox
            v-model="selectedDriver"
            :val="null"
            label="Unassign Driver"
            class="q-mt-sm"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" color="grey" v-close-popup />
          <q-btn
            flat
            label="Confirm Change"
            color="warning"
            @click="confirmReassignment"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Add the ViewTripDetails Dialog Component -->
    <ViewTripDetails
      :tripData="tripToView"
      :show.sync="viewTripDialogVisible"
    />
  </div>
  <!-- End of single root element -->
</template>

<style lang="scss" scoped>
.trip-timeline-container {
  max-height: 60vh;
  overflow-y: auto;
}

.trip-item {
  margin-bottom: 12px;
}

.trip-card {
  border-radius: 8px;
}

.status-badge {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
}

/* Add any other specific styles if needed */
</style>
